import * as React from "react";
import { observer } from "mobx-react";
import { Location } from "src/components/shared/Location/Location";
import { Dates } from "src/components/shared/Dates/Dates";
import { TimeDropdown } from "src/components/shared/TimeDropdown/TimeDropdown";
import { VendorCodes } from "./vendorOptions/VendorCodes";
import { CarsHiddenSearchData } from "./CarsHiddenSearchData";
import { CarTrawlerHiddenSearchData } from "./CarTrawlerHiddenSearchData";
import { CarsFormViewProps } from "../typings";
import { locKeys } from "./l10n";
import { DifferentDropOffToggle } from "./DifferentDropOffToggle";
import { DriverAgeInRangeToggle } from "./DriverAgeInRangeToggle";
import {
  UitkLayoutGrid,
  UitkLayoutConditionalGridSpan,
  UitkLayoutConditionalGridColumnRow,
} from "uitk-react-layout-grid";
import { SubmitRow } from "components/shared/StorefrontWizard/SubmitRow/SubmitRow";
import { LobErrorSummary } from "src/components/shared/LobErrorSummary/LobErrorSummary";
import DriverAgeInputField from "./DriversAgeInputField";
import { Viewport, ViewSmall, ViewMedium } from "@shared-ui/viewport-context";
import { AARPCheckbox } from "./AARPCheckbox";

export const CarsFormView: React.FunctionComponent<CarsFormViewProps> = observer((props) => {
  const allTMarginTwo = "all-t-margin-two";
  const {
    carWizardState,
    carConfig,
    isDiffDropOff,
    onDifferentDropOffSelected,
    isDriverAgeInRange,
    isAARPChecked,
    onDriverAgeInRange,
    onAARPChange,
    setInputsRefs,
  } = props;
  const { pickUpTimeLabel, dropOffTimeLabel } = locKeys;
  const format12hrConfig = carWizardState.config.timeFormats.timeFormat12Hour;
  const { isCarFormValid, updateInvalidFormsState } = carWizardState;
  const mainGridColumns: UitkLayoutConditionalGridColumnRow = { small: 2, medium: 12 };
  const locationColSpan: UitkLayoutConditionalGridSpan = { small: 2, medium: 6 };
  const datesColSpan: UitkLayoutConditionalGridSpan = { small: 2, medium: 12, large: 6 };
  const timeDropdownColSpan: UitkLayoutConditionalGridSpan = { small: 1, medium: 6, large: 3 };
  const couponColSpan: UitkLayoutConditionalGridSpan = { small: 2, medium: 3 };
  const driversAgeColSpan: UitkLayoutConditionalGridSpan = { small: 2, medium: 6 };

  if (isCarFormValid) {
    updateInvalidFormsState();
  }

  React.useEffect(() => {
    const VIEWPORT_MAX_SMALL_BREAKPOINT = 575;
    const { innerWidth: viewportSize } = window;

    if (viewportSize > VIEWPORT_MAX_SMALL_BREAKPOINT) {
      onDifferentDropOffSelected();
    }
  }, []);

  return (
    <>
      <UitkLayoutGrid columns={mainGridColumns} space="three" className={allTMarginTwo}>
        {!isCarFormValid && (
          <LobErrorSummary
            locHeadingToken={props.getLocError(false)!}
            locHeadingArgs={carWizardState.moreThanOneError() && [carWizardState.numberOfErrors]}
            locLinkToken={props.getLocError(true)}
            linkClickFocusId="id-roundtrip-error"
            inputErrorRef={carWizardState.errorInputRef}
            colSpan={mainGridColumns}
          />
        )}
        <Location
          setInputsRefs={setInputsRefs}
          originField={true}
          destinationField={false}
          lobState={carWizardState}
          colSpan={locationColSpan}
        />

        <Viewport>
          <ViewMedium>
            <Location originField={false} destinationField={true} lobState={carWizardState} colSpan={locationColSpan} />
          </ViewMedium>
        </Viewport>
      </UitkLayoutGrid>

      <Viewport>
        <ViewSmall>
          <>
            <UitkLayoutGrid columns={mainGridColumns} space="three" className={allTMarginTwo}>
              <DifferentDropOffToggle
                isDiffDropOff={isDiffDropOff}
                onDifferentDropOffSelected={onDifferentDropOffSelected}
              />
            </UitkLayoutGrid>

            <UitkLayoutGrid columns={mainGridColumns} space="three" className={allTMarginTwo}>
              {isDiffDropOff && (
                <Location
                  originField={false}
                  destinationField={true}
                  lobState={carWizardState}
                  colSpan={locationColSpan}
                />
              )}
            </UitkLayoutGrid>
          </>
        </ViewSmall>
        <ViewMedium />
      </Viewport>

      <UitkLayoutGrid columns={mainGridColumns} space="three" className={allTMarginTwo}>
        <Dates lobState={carWizardState} colSpan={datesColSpan} singleDateSelect={true} setInputsRefs={setInputsRefs} />
        <TimeDropdown
          labelKey={pickUpTimeLabel}
          timeValues={carWizardState.config.timeValues}
          onChange={carWizardState.setPickupTime}
          value={carWizardState.pickupTime}
          invalidKey={carWizardState.timeStartInvalidKey}
          colSpan={timeDropdownColSpan}
          use12hr={format12hrConfig}
        />
        <TimeDropdown
          labelKey={dropOffTimeLabel}
          timeValues={carWizardState.config.timeValues}
          onChange={carWizardState.setReturnTime}
          value={carWizardState.returnTime}
          colSpan={timeDropdownColSpan}
          use12hr={format12hrConfig}
        />

        {carConfig.driveAgeCheckbox && (
          <DriverAgeInRangeToggle
            isDriverAgeInRange={isDriverAgeInRange}
            onDriverAgeInRange={onDriverAgeInRange}
            carConfig={carConfig}
          />
        )}
        {carConfig.driveAgeCheckbox && !isDriverAgeInRange && (
          <DriverAgeInputField
            carWizardState={carWizardState}
            colSpan={driversAgeColSpan}
            setInputsRefs={setInputsRefs!}
          />
        )}

        {carConfig.AARPCheckbox && <AARPCheckbox isAARPChecked={isAARPChecked} onAARPChange={onAARPChange} />}
      </UitkLayoutGrid>

      {carConfig.showVendorOptions && (
        <VendorCodes carWizardState={carWizardState} mainColSpan={mainGridColumns} colSpan={couponColSpan} />
      )}

      <SubmitRow lobState={carWizardState} rfrr={"SearchClick_Cars"} />

      {!carConfig.isCarTrawler ? (
        <CarsHiddenSearchData lobState={carWizardState} />
      ) : (
        <CarTrawlerHiddenSearchData lobState={carWizardState} />
      )}
    </>
  );
});
